/**
 * Pazarlama çalışma merkezi — "kaynaktan sonraki işe" (24 Ekim 2026). Sözleşme: docs/spec/castinflow/pazarlama-calisma-merkezi.md.
 * `GET /marketing/workcenter` — yalnız okuma. Ölçüler, huni, kaynak dağılımı ve dönemsel gelişim AYNI tanımla kurulur:
 *
 * - Kaynak kayıt türü **aday**dır (`lead`); dönem adayın oluşturulma anıdır ve çalışma alanının saat diliminde yorumlanır
 *   (kampanya raporuyla aynı tanım: `campaignReportQuerySchema`). Kampanya süzgeci seçilen atıf modelinin (ilk/son temas) kök
 *   kampanyasıdır.
 * - Her sayı, yanıttaki `filter` (FilterAst) ile **liste derleyicisinin** aynı ACL'li sayımıdır. İstemci kartı/çubuğu
 *   `/records/lead?suzgec=<encodeListFilter(filter)>` adresine bağlar; hedef liste aynı süzgeci görünür ve temizlenebilir
 *   kriter olarak yükler → kart sayısı = liste toplamı (aynı an, aynı yetki). Sıfır sonuç da boş listeye gider.
 * - Para birimleri toplanmaz; kazanılan ciro para birimi başına ayrı satırdır ve tahsilat DEĞİLDİR. Manuel satış potansiyeli
 *   ölçülmüş kazanma olasılığı değildir. Karşılaştırma dönemi yoktur (sahte artış/azalış oku üretilmez).
 */
import { z } from 'zod';
import { ATTRIBUTION_MODELS } from './acquisition.js';
import { filterAstSchema } from './list-query.js';
import { moneyAmountSchema } from './today-insights.js';
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const count = z.number().int().min(0);
export const MARKETING_LIMITS = {
    /** En uzun dönem (gün) — kampanya raporuyla aynı tavan */
    maxRangeDays: 400,
    /** Varsayılan dönem (gün; bugün dahil) */
    defaultRangeDays: 30,
    /** Dönemsel gelişim: bu günden uzun dönemde haftalık kova */
    dailyBucketMaxDays: 45,
    /** Çalışma kuyruğu önizlemesinde en çok satır (tamamı listede) */
    queuePreview: 8,
};
export const marketingWorkcenterQuerySchema = z
    .object({
    /** Dahil başlangıç ve bitiş günü (çalışma alanı saat dilimi). Verilmezse son 30 gün. */
    from: isoDate.optional(),
    to: isoDate.optional(),
    model: z.enum(ATTRIBUTION_MODELS).default('last_touch'),
    /**
     * 2. tur (§ 6): SEÇİLEN MODELİN temas kaynağı (`firstTouchSource`/`lastTouchSource`; temas yoksa adayın kayıt kaynağı —
     * kampanya raporuyla aynı COALESCE kuralı). Tenant sözlüğü anahtarı.
     */
    source: z.string().min(1).max(64).optional(),
    /** 2. tur (§ 6): adayın KAYIT kaynağı (`sourceKey`, "Kayıt kaynağı"); `source` ile birlikte verilebilir */
    recordSource: z.string().min(1).max(64).optional(),
    /** Kök kampanya kimliği (seçilen modelin temas kampanyası); `none` = kampanyası bilinmeyen */
    campaign: z.string().min(1).max(200).optional(),
})
    .strict();
/**
 * Ölçüler (2. tur anlam sözleşmesi § 2). Küme = dönemde OLUŞTURULAN adaylar + kapsam süzgeçleri; her ölçü kümenin BUGÜNKÜ
 * durumunu sayar ("dönemde gelen adaylar ve sonuçları"; dönemde gerçekleşen satış değil). "Açık aday" iş kuralı dağıtım
 * kapasitesi ve kimlik eşleştirmesiyle aynıdır: dönüştürülmemiş ve durumu niteliksiz/tekrar eden değil.
 * - `incoming`: küme (sonraki durumlar toplamdan düşmez)
 * - `new`: durum `new` ve dönüştürülmemiş
 * - `unassigned`: sorumlusu yok ve açık
 * - `awaitingReply`: bağlı, kapanmamış, müşteri yanıtı bekleyen ve çağıranın görebildiği konuşması olan
 * - `priority`: güncel manuel potansiyel ≥ tenant eşiği ve açık; potansiyel politikası kapalıysa ölçü YOK
 * - `qualified`: durum tenant'ın nitelikli durumlarından biri (dönüştürülmüş olmak tek başına sayılmaz)
 * - `converted`: dönüştüğü FIRSAT ilişkisi var ve çağıran o fırsatı görebiliyor (`convertedOpportunityStatus` dolu); yalnız
 *   kişi/şirkete dönüşen ya da fırsatı silinmiş/görülemeyen aday sayılmaz
 * - `won`: dönüştüğü fırsat kazanıldı (aynı fırsat görünürlüğü) — `won ⊆ converted`
 */
export const MARKETING_METRIC_KEYS = [
    'incoming',
    'new',
    'unassigned',
    'awaitingReply',
    'priority',
    'qualified',
    'converted',
    'won',
];
/** Liste derleyicisinin sayımı; `exact=false` ise sayım tavana takıldı (`count` alt sınırdır, "N+" gösterilir) */
const listCountSchema = z.object({
    count,
    exact: z.boolean(),
    /** Hedef listenin süzgeci (aday kaynağı) */
    filter: filterAstSchema,
});
export const marketingMetricSchema = listCountSchema.extend({ key: z.enum(MARKETING_METRIC_KEYS) });
/**
 * Süreç görünümü (§ 3; huni DEĞİL): bağımsız durum sayıları — her satır kümenin o koşulu sağlayan kısmıdır; sonraki aşamadaki
 * kayıt önceki adımlardan geçmiş SAYILMAZ (ör. temas kaydı olmadan dönüştürülmüş aday). Nitelikli = `qualified` ölçüsü.
 * "Nitelikli aşamasına ulaşmış (geçmiş)" ölçüsü YOKTUR: aday durum geçmişi güvenilir değil (belge § 3).
 */
export const MARKETING_PROCESS_STAGES = [
    'incoming',
    'contacted',
    'qualified',
    'converted',
    'won',
];
/**
 * Oranlar (§ 4): pay ve payda AYNI kümenin ölçüleridir ve her zaman yazılır. Payda 0 → `value: null` ("hesaplanacak kayıt yok").
 * Para birimleri oranlara girmez.
 */
export const MARKETING_RATE_KEYS = ['leadToOpportunity', 'opportunityToWon', 'leadToWon'];
export const MARKETING_RATE_DEFINITIONS = {
    leadToOpportunity: {
        numerator: 'converted',
        denominator: 'incoming',
        numeratorLabel: 'fırsata dönüşen aday',
        denominatorLabel: 'gelen aday',
    },
    opportunityToWon: {
        numerator: 'won',
        denominator: 'converted',
        numeratorLabel: 'satışa dönüşen aday',
        denominatorLabel: 'fırsata dönüşen aday',
    },
    leadToWon: {
        numerator: 'won',
        denominator: 'incoming',
        numeratorLabel: 'satışa dönüşen aday',
        denominatorLabel: 'gelen aday',
    },
};
export const marketingRateSchema = z.object({
    key: z.enum(MARKETING_RATE_KEYS),
    numerator: count,
    denominator: count,
    /** Pay ve payda kesin mi (ikisinden biri tavana takıldıysa oran yaklaşıktır) */
    exact: z.boolean(),
    /** pay / payda (0–1); payda 0 ise null */
    value: z.number().min(0).max(1).nullable(),
    numeratorLabel: z.string(),
    denominatorLabel: z.string(),
});
/**
 * Kampanya raporunun oran sütunları (§ 4) — rapor kümeleri: aday/nitelikli adayın temasıyla, fırsat/kazanılan FIRSATIN kendi
 * temasıyla (dönüşüm anının görüntüsü; yeniden yazılmaz) gruplanır. Rapordaki fırsat sayısı "adaydan dönüşen" değildir; oranlar
 * yalnız aynı satırın sütunlarından ve bu tanımla yazılır.
 */
export const CAMPAIGN_REPORT_RATE_DEFINITIONS = {
    qualifiedRate: {
        numerator: 'qualified',
        denominator: 'leads',
        numeratorLabel: 'nitelikli aday',
        denominatorLabel: 'aday',
    },
    opportunityToWon: {
        numerator: 'won',
        denominator: 'opportunities',
        numeratorLabel: 'kazanılan fırsat',
        denominatorLabel: 'fırsat',
    },
};
// Kaynak çözümleme durumu (§ 5): `source-state.ts` (kampanya raporu şeması da kullanır; modül döngüsü kurulmasın)
export { DELETED_SOURCE_LABEL, SOURCE_STATES, SOURCE_STATE_LABELS, resolveSource, } from './source-state.js';
import { SOURCE_STATES, resolveSource } from './source-state.js';
/** Geriye uyum (2. tur): `resolveSource` (kanıtsız eksik anahtar artık "Kaynak bilgisi bulunamadı") */
export function resolveSourceLabel(lookups, key, deletedKeys) {
    const r = resolveSource(lookups, key, deletedKeys);
    return { label: r.label, active: r.active };
}
const sourceRefSchema = z.object({
    key: z.string(),
    label: z.string(),
    active: z.boolean().nullable(),
    sourceState: z.enum(SOURCE_STATES),
});
export const marketingWorkcenterSchema = z.object({
    period: z.object({ from: isoDate, to: isoDate, timeZone: z.string(), days: z.number().int() }),
    model: z.enum(ATTRIBUTION_MODELS),
    /** Uygulanan süzgeçler (sunucuda çözülmüş). `source` = seçilen modelin temas kaynağı; `recordSource` = kayıt kaynağı */
    scope: z.object({
        source: sourceRefSchema.nullable(),
        recordSource: sourceRefSchema.nullable(),
        campaign: z.object({ id: z.string(), label: z.string() }).nullable(),
    }),
    metrics: z.array(marketingMetricSchema),
    /** Tenant potansiyel öncelik eşiği (yüzde); politika kapalıysa null */
    potentialThreshold: z.number().int().min(0).max(100).nullable(),
    /** Süreç görünümü (§ 3) — bağımsız durum sayıları */
    process: z.array(listCountSchema.extend({ stage: z.enum(MARKETING_PROCESS_STAGES) })),
    rates: z.array(marketingRateSchema),
    /**
     * Kaynak dağılımı — SEÇİLEN MODELİN temas kaynağına göre (§ 6; temas yoksa adayın kayıt kaynağı, kampanya raporuyla aynı
     * kural). Hedef süzgeç `firstTouchSource`/`lastTouchSource`. `active` geriye uyum içindir; anlam `sourceState`'tedir.
     */
    sources: z.array(listCountSchema.extend(sourceRefSchema.shape)),
    /** "Kayıt kaynağı" kırılımı (adayın `sourceKey`'i; hedef süzgeç `sourceKey`) */
    recordSources: z.array(listCountSchema.extend(sourceRefSchema.shape)),
    trend: z.object({
        bucket: z.enum(['day', 'week']),
        points: z.array(listCountSchema.extend({ start: isoDate, end: isoDate, partial: z.boolean() })),
    }),
    /** Dönem adaylarından kazanılan fırsatların gerçekleşen/ciro tutarı — para birimi başına; tahsilat değildir */
    wonRevenue: z.array(moneyAmountSchema),
    /** Sayımların üretildiği an (UTC) */
    generatedAt: z.string(),
});
/** Kampanya süzgecinde "kampanyası bilinmeyen" seçimi (`?campaign=none`) ve görünen adı */
export const MARKETING_NO_CAMPAIGN = 'none';
export const MARKETING_NO_CAMPAIGN_LABEL = 'Kampanyası bilinmeyen';
// ---------------------------------------------------------------------------------------------------------------------
// Adresle süzgeç taşıma (`?suzgec=`): FilterAst'in base64url JSON kodlaması. Liste sayfası bunu geçici (kaydedilmemiş)
// kriter olarak yükler; doğrulama sunucudaki liste sorgusunda yine yapılır (kodlama yetki taşımaz).
// ---------------------------------------------------------------------------------------------------------------------
export const LIST_FILTER_PARAM = 'suzgec';
function toBase64Url(s) {
    const bytes = new TextEncoder().encode(s);
    let bin = '';
    for (const b of bytes)
        bin += String.fromCharCode(b);
    return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function fromBase64Url(s) {
    const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
}
export function encodeListFilter(ast) {
    return toBase64Url(JSON.stringify(ast));
}
/** Geçersiz/bozuk değerde `null` (sayfa süzgeçsiz açılır ve bunu kullanıcıya söyler) */
export function decodeListFilter(raw) {
    if (raw === null || raw === undefined || raw === '' || raw.length > 8000)
        return null;
    try {
        const parsed = filterAstSchema.safeParse(JSON.parse(fromBase64Url(raw)));
        return parsed.success ? parsed.data : null;
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=marketing.js.map